import React, { useEffect, useRef, useState } from "react";
import { FaRegCircle } from "react-icons/fa6";
import { GrRadialSelected } from "react-icons/gr";
import { clsxm } from "@/utils/clsxm";
import { TreatmentLabel } from "@/pages/quiz/base/questions/selectTreatmentOptionNew/components/TreatmentLabel";
import { Accordion } from "@/pages/quiz/base/questions/selectTreatmentOptionNew/components/Accordion";

export const Treatment = ({
  isSelected,
  title,
  isNew,
  img,
  onSelect,
  price,
  labels,
  titleDetails,
  details,
}: {
  isSelected: boolean;
  title: string;
  isNew?: boolean;
  img: string;
  onSelect: () => void;
  price: string;
  labels: {
    id: number;
    text: string;
    textColor: string;
    bgColor: string;
  }[];
  titleDetails: string;
  details: {
    id: number;
    title: string;
    text: string | React.ReactNode;
  }[];
}) => {
  const [measuredHeight, setMeasuredHeight] = useState<number>(0);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const roRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    if (isSelected && innerRef.current) {
      const id = requestAnimationFrame(() => {
        setMeasuredHeight(innerRef.current!.scrollHeight);
      });
      return () => cancelAnimationFrame(id);
    }
    setMeasuredHeight(0);
  }, [isSelected]);

  useEffect(() => {
    if (!innerRef.current) return;
    roRef.current = new ResizeObserver(() => {
      if (isSelected && innerRef.current) {
        setMeasuredHeight(innerRef.current.scrollHeight);
      }
    });
    roRef.current.observe(innerRef.current);
    return () => {
      roRef.current?.disconnect();
      roRef.current = null;
    };
  }, [isSelected]);

  return (
    <div
      onClick={onSelect}
      className={clsxm(
        "cursor-pointer w-full border rounded-xl border-gray20",
        {
          "bg-purple-light-v2 border-purple10": isSelected,
        }
      )}
      aria-expanded={isSelected}
      role="button"
    >
      <div
        className={clsxm("w-full flex gap-2 items-center justify-between p-1", {
          "pb-0": isSelected,
        })}
      >
        <div className="flex items-center justify-start gap-2 pl-3 py-5">
          {isSelected ? (
            // @ts-ignore
            <GrRadialSelected size={22} className="text-purple10" />
          ) : (
            // @ts-ignore
            <FaRegCircle size={22} color="#1F1F1F7A" />
          )}
          <h4 className="font-bold leading-[100%] text-black md:ml-2.5">
            {title}
          </h4>

          <div className="hidden sm:block">
            {isNew && (
              <span className="px-2.5 py-2 bg-[#A83A961A] rounded-[35px] text-[#A83A96] text-[10px] font-medium">
                New
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-4 items-center">
          <div className="block sm:hidden">
            {isNew && (
              <span className="px-2.5 py-2 bg-[#A83A961A] rounded-[35px] text-[#A83A96] text-[10px] font-medium">
                New
              </span>
            )}
          </div>

          <div
            className={clsxm(
              "w-[76px] h-[76px] max-w-[76px] rounded-lg bg-purple-light-v2",
              {
                "bg-transparent": isSelected,
              }
            )}
          >
            <img src={img} alt={title} className="w-full h-full object-contain" />
          </div>
        </div>
      </div>

      <div
        className={clsxm(
          "overflow-hidden transition-all",
          isSelected ? "border-t border-gray20" : "border-t border-transparent"
        )}
        style={{
          maxHeight: isSelected ? measuredHeight : 0,
          opacity: isSelected ? 1 : 0,
          transition:
            "max-height 300ms ease, opacity 250ms ease, border-color 200ms ease",
        }}
        aria-hidden={!isSelected}
      >
        <div ref={innerRef}>
          <div className="px-4 text-black sm:px-0 py-6 sm:max-w-[350px] mx-auto flex flex-col items-start justify-start gap-4">
            <div
              className="text-sm font-medium [&>b]:font-bold leading-[100%]"
              dangerouslySetInnerHTML={{
                __html: price,
              }}
            />
            <div className="!mt-auto flex gap-x-1 sm:gap-x-2 gap-y-1 flex-wrap whitespace-nowrap text-sm sm:text-base">
              {labels.map((l) => (
                <TreatmentLabel key={l.id} {...l} />
              ))}
            </div>
            <p className="text-sm leading-[140%]">{titleDetails}</p>
            <div className="flex flex-col w-full">
              {details.map((d, idx) => (
                <Accordion key={idx} title={d.title} body={d.text} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
